import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminColor } from '@/hooks/admin/useAdmin';
import { useColors, useDeleteColor } from '@/hooks/admin/useAdminColors';
import ColorModal from '@/modals/admin/ColorModal';

const ColorsAdmin: React.FC = () => {
    const [selectedColor, setSelectedColor] = useState<AdminColor | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');

    const { data: colors = [], isLoading, error } = useColors();
    const deleteColor = useDeleteColor();

    const filteredColors = colors.filter(color => {
        const matchesSearch = color.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterActive === 'all' ||
                            (filterActive === 'active' && color.is_active) ||
                            (filterActive === 'inactive' && !color.is_active);
        return matchesSearch && matchesFilter;
    });

    const handleCreateColor = () => {
        setSelectedColor(undefined);
        setModalOpen(true);
    };

    const handleEditColor = (color: AdminColor) => {
        setSelectedColor(color);
        setModalOpen(true);
    };

    const handleDeleteColor = async (color: AdminColor) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el color "${color.name}"?`)) {
            try {
                await deleteColor.mutateAsync(color.id);
            } catch (error) {
                console.error('Error deleting color:', error);
                alert('Error al eliminar el color');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedColor(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando colores...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar los colores</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Colores</h1>
                        <p className="admin-page-subtitle">
                            Administra los colores disponibles para los productos
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateColor}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nuevo Color
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar colores..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>
                </div>
            </div>

            {/* Colors Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Color</th>
                            <th>Nombre</th>
                            <th>Código Hex</th>
                            <th>Costo Adicional</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredColors.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all'
                                        ? 'No se encontraron colores con los filtros aplicados'
                                        : 'No hay colores registrados'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredColors.map(color => (
                                <tr key={color.id}>
                                    <td className="admin-table-color">
                                        <div className="admin-color-sample">
                                            <div
                                                className="admin-color-circle"
                                                style={{ backgroundColor: color.hex_code }}
                                            />
                                        </div>
                                    </td>
                                    <td className="admin-table-name">
                                        {color.name}
                                    </td>
                                    <td className="admin-table-hex">
                                        <code>{color.hex_code}</code>
                                    </td>
                                    <td className="admin-table-price">
                                        ${color.additional_cost.toFixed(2)}
                                    </td>
                                    <td>
                                        <span className={`admin-status ${color.is_active ? 'active' : 'inactive'}`}>
                                            {color.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditColor(color)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteColor(color)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteColor.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Color Modal */}
            <ColorModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                color={selectedColor}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default ColorsAdmin;
